/**
 * Determine the mouse position
 *
 * @param event the canvas event
 * @returns *[] tuple of position x,y
 * @private
 */
export const pointerPosition = event => {
  event = event || window.event;
  let target = event.target || event.srcElement,
    style = target.currentStyle || window.getComputedStyle(target, null),
    borderLeftWidth = parseInt(style["borderLeftWidth"], 10),
    borderTopWidth = parseInt(style["borderTopWidth"], 10),
    rect = target.getBoundingClientRect(),
    _x = event.clientX - borderLeftWidth - rect.left,
    _y = event.clientY - borderTopWidth - rect.top,
    _touchX = event.changedTouches
      ? event.changedTouches[0].clientX - borderLeftWidth - rect.left
      : null,
    _touchY = event.changedTouches
      ? event.changedTouches[0].clientY - borderTopWidth - rect.top
      : null;
  return [_x || _touchX, _y || _touchY];
};

/**
 * Calculate the distance of two x,y points
 *
 * @param point1 an object with x,y attributes representing the start point
 * @param point2 an object with x,y attributes representing the end point
 *
 * @returns {number}
 */
export const linearDistance = (point1, point2) => {
  let xs = point2.x - point1.x;
  let ys = point2.y - point1.y;
  return Math.sqrt(xs * xs + ys * ys);
};

/**
 * Return a random uuid of the form xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * @returns {string}
 */
export const uuid4 = () => {
  let uuid = "",
    ii;
  for (ii = 0; ii < 32; ii += 1) {
    switch (ii) {
      case 8:
      case 20:
        uuid += "-";
        uuid += ((Math.random() * 16) | 0).toString(16);
        break;
      case 12:
        uuid += "-";
        uuid += "4";
        break;
      case 16:
        uuid += "-";
        uuid += ((Math.random() * 4) | 8).toString(16);
        break;
      default:
        uuid += ((Math.random() * 16) | 0).toString(16);
    }
  }
  return uuid;
};

/**
 * Longest edge, in pixels, that an imported bitmap is allowed to keep.
 * Imported images are stored inline as base64 inside the Fabric JSON for every
 * frame they appear in, and that JSON is what gets serialised into Redux, run
 * through the LZ compressor by redux-persist, and uploaded to S3 on save. A
 * full-resolution screenshot is therefore paid for over and over again, so it is
 * worth resampling once at import time.
 */
const MAX_IMPORTED_IMAGE_EDGE = 1280;
const IMPORTED_JPEG_QUALITY = 0.85;

/**
 * True if any pixel in the context is not fully opaque.
 * Run against the already-downscaled canvas so this stays cheap.
 */
const hasTransparency = (ctx, width, height) => {
  let data;
  try {
    data = ctx.getImageData(0, 0, width, height).data;
  } catch (e) {
    // Tainted canvas (cross-origin source) -- assume alpha and keep PNG.
    return true;
  }
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] !== 255) return true;
  }
  return false;
};

/**
 * Resample a data URL so its longest edge is at most MAX_IMPORTED_IMAGE_EDGE,
 * re-encoding to JPEG when the image is fully opaque (which is the common case
 * for photos and screenshots, and where nearly all of the saving comes from).
 *
 * The callback receives the new data URL plus the scale factor that was applied,
 * so the caller can compensate and keep the on-canvas geometry unchanged.
 * Anything that is not a loadable image -- the .txt / .ish import path also
 * routes through here -- falls back to the original input untouched.
 *
 * @param dataUrl source data URL
 * @param cb called with (dataUrl, appliedScale)
 */
export const downscaleImageDataUrl = (dataUrl, cb) => {
  if (typeof dataUrl !== "string" || dataUrl.indexOf("data:image/") !== 0) {
    return cb(dataUrl, 1);
  }

  const img = new Image();

  img.onload = () => {
    const longestEdge = Math.max(img.width, img.height);

    if (!longestEdge || longestEdge <= MAX_IMPORTED_IMAGE_EDGE) {
      return cb(dataUrl, 1);
    }

    const ratio = MAX_IMPORTED_IMAGE_EDGE / longestEdge;
    const width = Math.max(1, Math.round(img.width * ratio));
    const height = Math.max(1, Math.round(img.height * ratio));

    const off = document.createElement("canvas");
    off.width = width;
    off.height = height;
    const ctx = off.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);

    try {
      const resampled = hasTransparency(ctx, width, height)
        ? off.toDataURL("image/png")
        : off.toDataURL("image/jpeg", IMPORTED_JPEG_QUALITY);
      // Only take the resampled version if it actually came out smaller.
      if (resampled.length < dataUrl.length) return cb(resampled, ratio);
    } catch (e) {
      // toDataURL can throw on a tainted canvas -- fall through to the original.
    }
    return cb(dataUrl, 1);
  };

  // Not an image we can decode (or a malformed data URL) -- behave as before.
  img.onerror = () => cb(dataUrl, 1);

  img.src = dataUrl;
};
